import { useEffect, useMemo, useRef } from "react";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";

import { alignSeries, lonelyPoints } from "./align";

export type Series = { label: string; color: string; points: Array<[number, number | null]> };

/** Multi-series time chart on uPlot (small and fast with many series). */
export function TimeSeries({ series, height = 180, unit = "" }: { series: Series[]; height?: number; unit?: string }) {
  const el = useRef<HTMLDivElement>(null);
  const plot = useRef<uPlot | null>(null);

  const data = useMemo(() => alignSeries(series.map((s) => s.points)), [series]);

  useEffect(() => {
    if (!el.current) return;
    const axis = { stroke: "#8b90a0", grid: { stroke: "rgba(255,255,255,0.05)" }, ticks: { stroke: "rgba(255,255,255,0.08)" } };
    const opts: uPlot.Options = {
      width: el.current.clientWidth || 600,
      height,
      legend: { show: true },
      cursor: { drag: { x: true, y: false } },
      scales: { x: { time: true } },
      axes: [axis, { ...axis, values: (_u, vals) => vals.map((v) => (v == null ? "" : `${v}${unit}`)) }],
      series: [{}, ...series.map((s) => ({
        label: s.label, stroke: s.color, width: 1.5, spanGaps: false,
        // No markers, except on values no line reaches (between gaps).
        points: { show: false, size: 5, filter: (u: uPlot, i: number) => {
          const idxs = lonelyPoints(u.data[i]);
          return idxs.length ? idxs : null;
        } },
      }))],
    };
    plot.current = new uPlot(opts, data, el.current);
    const ro = new ResizeObserver(() => {
      if (el.current && plot.current) plot.current.setSize({ width: el.current.clientWidth, height });
    });
    ro.observe(el.current);
    return () => {
      ro.disconnect();
      plot.current?.destroy();
      plot.current = null;
    };
    // Rebuild when the set of series changes; data updates go through setData.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series.map((s) => s.label + s.color).join("|"), height, unit]);

  useEffect(() => {
    plot.current?.setData(data);
  }, [data]);

  return <div ref={el} className="atlas-uplot w-full" />;
}

/** Tiny inline trend line for dense tables. */
export function Sparkline({ values, width = 96, height = 22, color = "currentColor" }: { values: Array<number | null>; width?: number; height?: number; color?: string }) {
  const nums = values.filter((v): v is number => v !== null);
  if (nums.length < 2) return <span className="inline-block text-xs text-muted-foreground">—</span>;
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const span = max - min || 1;
  const step = width / (values.length - 1);
  let d = "";
  values.forEach((v, i) => {
    if (v === null) return;
    const x = i * step;
    const y = height - 2 - ((v - min) / span) * (height - 4);
    d += `${d === "" || values[i - 1] === null ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
  });
  return (
    <svg width={width} height={height} className="overflow-visible" aria-hidden>
      <path d={d} fill="none" stroke={color} strokeWidth={1.4} />
    </svg>
  );
}
